import { useCallback, useEffect, useRef, useState } from 'react'
import {
    getNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead
} from 'actions/collaborativeTasks'

const POLL_INTERVAL_MS = 30 * 1000
const DROPDOWN_LIMIT = 5

/**
 * Loads the bell's notification list + unread count, and re-polls every 30s
 * while mounted. Also exposes one-shot mark-read / mark-all-read.
 *
 * Polling intentionally pauses when the page is hidden — keeps tabs quiet
 * when the user has switched away.
 */
/**
 * request() rejects with the raw Response when silentOnError is set, so read
 * the status and body here rather than letting the failure vanish.
 */
const describeFailure = async (reason) => {
    if (!reason) return 'Network error'
    const status = reason.status
    let detail = ''
    try {
        const body = await reason.json?.()
        detail = body?.message || body?.error || ''
    } catch (e) {
        // Body already consumed or not JSON.
    }
    const message = status ? `Notifications request failed (${status})${detail ? `: ${detail}` : ''}`
                           : 'Notifications request failed'
    // eslint-disable-next-line no-console
    console.error('[notifications]', message, reason)
    return message
}

export const useNotifications = ({ enabled = true } = {}) => {
    const [items, setItems] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const timerRef = useRef(null)

    const fetchData = useCallback(async () => {
        if (!enabled) return
        setLoading(true)
        // allSettled, not all: the list and the count are independent, and a
        // failing count must not discard a list that loaded fine.
        const [listOutcome, countOutcome] = await Promise.allSettled([
            getNotifications({ limit: DROPDOWN_LIMIT })(),
            getUnreadNotificationCount()()
        ])

        if (listOutcome.status === 'fulfilled') {
            const listRes = listOutcome.value
            const listPayload = listRes?.response || listRes
            setItems(Array.isArray(listPayload?.items) ? listPayload.items : [])
            setError(null)
        } else {
            // These calls are made with silentOnError, so nothing else reports
            // them. Without this the bell renders an empty list and a genuine
            // failure is indistinguishable from having no notifications.
            setError(await describeFailure(listOutcome.reason))
        }

        if (countOutcome.status === 'fulfilled') {
            const countRes = countOutcome.value
            const countPayload = countRes?.response ?? countRes
            setUnreadCount(typeof countPayload === 'number' ? countPayload : (countPayload?.unreadCount || 0))
        }

        setLoading(false)
    }, [enabled])

    // Initial load + polling
    useEffect(() => {
        if (!enabled) return
        fetchData()
        const start = () => {
            stop()
            timerRef.current = setInterval(() => {
                if (document.visibilityState === 'visible') fetchData()
            }, POLL_INTERVAL_MS)
        }
        const stop = () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
        start()
        const onVis = () => { if (document.visibilityState === 'visible') fetchData() }
        document.addEventListener('visibilitychange', onVis)
        return () => { stop(); document.removeEventListener('visibilitychange', onVis) }
    }, [enabled, fetchData])

    const markRead = useCallback(async (notificationId) => {
        // Optimistic UI flip — server call is idempotent.
        setItems(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n))
        setUnreadCount(prev => Math.max(0, prev - 1))
        try {
            await markNotificationRead(notificationId)()
        } catch (e) {
            // No revert: refetch on next poll will reconcile.
        }
    }, [])

    const markAll = useCallback(async () => {
        setItems(prev => prev.map(n => ({ ...n, read: true })))
        setUnreadCount(0)
        try {
            await markAllNotificationsRead()()
            fetchData()
        } catch (e) {
            fetchData()
        }
    }, [fetchData])

    return { items, unreadCount, loading, error, refetch: fetchData, markRead, markAll }
}
